/**
 * EmployeeBuilder — form-based composer for Step 1 launch input.
 *
 * Replaces the JSON textarea with a proper UI: per-employee form with
 * supervisor info, level, OKR items, KPI items. Maintains a list state
 * that the parent reads at Launch time.
 *
 * "Import from JSON" toggle is kept for power users who already have a
 * payload prepared, but it's collapsed by default.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Button from '../ui/Button';
import type { LaunchStep1Input } from '../../services/hrCycleLauncher';
import type { DirectoryEmployee } from '../../types/magicLink';
import {
  fetchKrsForEmployee, toOkrSeed,
  type SyncedKr, type EprPeriod, type EmployeeLevelForSync,
  type FetchKrsForEmployeeResult,
} from '../../services/okrTrackerSyncService';
import {
  listUnits, fuzzyMatchUnit, type OkrTrackerUnit,
} from '../../services/okrTrackerUnitsService';
import CascadeTimeline from './CascadeTimeline';

type Employee = LaunchStep1Input['employees'][number];
type Level = Employee['level'];

const LEVEL_OPTIONS: Array<{ value: Level; label: string }> = [
  { value: 'general',    label: 'General (skip Leadership)' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'dept_head',  label: 'Department Head' },
];

const LEVEL_LABELS: Record<Level, string> = {
  general:    'General',
  supervisor: 'Supervisor',
  dept_head:  'Department Head',
};

const emptyEmployee = (): Employee => ({
  employeeId:      '',
  employeeEmail:   '',
  employeeName:    '',
  supervisorId:    '',
  supervisorEmail: '',
  supervisorName:  '',
  level:           'general',
  okrItems: [{ id: `kr-${Date.now()}`, name: '', target: '' }],
  kpiItems: [{ id: `kpi-${Date.now()}`, name: '', target: '' }],
});

export interface EmployeeBuilderProps {
  value: Employee[];
  onChange: (next: Employee[]) => void;
  /**
   * Optional directory. When provided, the form replaces the manual ID /
   * email / name fields with a search-and-pick interface. The supervisor
   * auto-resolves from directManagerId. Falls back to manual entry when
   * the directory is empty or undefined.
   */
  directory?: DirectoryEmployee[];
  /**
   * Cycle year — passed to the OKR Tracker sync to filter KRs by
   * `objectives.cycle`. When omitted, the sync button is hidden.
   */
  cycleYear?: number;
  /**
   * Cycle period — drives the quarter overlap filter (H1 → Q1+Q2 etc.).
   */
  cyclePeriod?: EprPeriod;
}

const EmployeeBuilder: React.FC<EmployeeBuilderProps> = ({
  value, onChange, directory, cycleYear, cyclePeriod,
}) => {
  const [draft, setDraft] = useState<Employee | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');

  const startAdd = useCallback(() => {
    setDraft(emptyEmployee());
    setEditIdx(null);
  }, []);

  const startEdit = useCallback((idx: number) => {
    setDraft({ ...value[idx] });
    setEditIdx(idx);
  }, [value]);

  const cancelDraft = useCallback(() => {
    setDraft(null);
    setEditIdx(null);
  }, []);

  const saveDraft = useCallback(() => {
    if (!draft) return;
    if (!validateEmployee(draft)) return;
    // Drop placeholder OKR/KPI rows the user never filled — an unnamed
    // item would otherwise become a scoreable row on the live form.
    const cleaned: Employee = {
      ...draft,
      okrItems: draft.okrItems.filter(it => (it.name ?? '').trim() !== ''),
      kpiItems: draft.kpiItems.filter(it => (it.name ?? '').trim() !== ''),
    };
    const next = [...value];
    if (editIdx !== null) next[editIdx] = cleaned;
    else next.push(cleaned);
    onChange(next);
    setDraft(null);
    setEditIdx(null);
  }, [draft, editIdx, value, onChange]);

  const removeAt = useCallback((idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  }, [value, onChange]);

  const applyJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText) as Employee[];
      if (!Array.isArray(parsed)) throw new Error('Must be an array');
      onChange(parsed);
      setShowJson(false);
      setJsonText('');
    } catch (e) {
      alert(`Invalid JSON: ${(e as Error).message}`);
    }
  }, [jsonText, onChange]);

  return (
    <div className="space-y-3">
      {/* Employee list */}
      {value.length === 0 && !draft && (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">
          No employees added yet. Click <b>Add employee</b> to start.
        </div>
      )}

      {value.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">
            Employees in this launch ({value.length})
          </div>
          {value.map((e, idx) => (
            <div
              key={`${e.employeeId}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 rounded-full bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-stone-900">
                    {e.employeeName || '(unnamed)'}{' '}
                    <span className="text-xs font-normal text-stone-500">
                      · {e.employeeEmail || 'no email'}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {e.position ? `${e.position} · ` : ''}
                    {e.department ? `${e.department} · ` : ''}
                    Supervisor: {e.supervisorName || '(none)'}
                    {' · '}
                    {e.okrItems.length > 0
                      ? `${e.okrItems.length} OKR (synced)`
                      : 'OKRs: employee fills'}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="tertiary" icon="edit" onClick={() => startEdit(idx)}>Edit</Button>
                <Button variant="tertiary" icon="delete" onClick={() => removeAt(idx)}>Remove</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit form */}
      {draft && (
        <EmployeeForm
          value={draft}
          onChange={setDraft}
          onCancel={cancelDraft}
          onSave={saveDraft}
          isEdit={editIdx !== null}
          directory={directory}
          cycleYear={cycleYear}
          cyclePeriod={cyclePeriod}
        />
      )}

      {!draft && (
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" icon="person_add" onClick={startAdd}>
            Add employee
          </Button>
          <button
            type="button"
            onClick={() => setShowJson(s => !s)}
            className="text-xs text-stone-500 hover:text-stone-800 underline"
          >
            {showJson ? 'Hide JSON import' : 'Import from JSON'}
          </button>
        </div>
      )}

      {showJson && !draft && (
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2">
          <div className="text-xs text-stone-600">
            Paste a JSON array of <code>LaunchStep1EmployeeInput</code>.
            Replaces the current list.
          </div>
          <textarea
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            rows={6}
            className="w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs font-mono"
            placeholder='[{ "employeeId": "...", "employeeEmail": "...", ... }]'
          />
          <div className="flex justify-end gap-2">
            <Button variant="tertiary" onClick={() => setShowJson(false)}>Cancel</Button>
            <Button variant="secondary" onClick={applyJson}>Apply</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Per-employee form ────────────────────────────────────────────

const EmployeeForm: React.FC<{
  value: Employee;
  onChange: (e: Employee) => void;
  onCancel: () => void;
  onSave: () => void;
  isEdit: boolean;
  directory?: DirectoryEmployee[];
  cycleYear?: number;
  cyclePeriod?: EprPeriod;
}> = ({ value, onChange, onCancel, onSave, isEdit, directory, cycleYear, cyclePeriod }) => {
  const update = (patch: Partial<Employee>) => onChange({ ...value, ...patch });
  const useDirectory = directory && directory.length > 0;

  // Restore "sync" mode when re-editing an employee that already has
  // OKR rows — otherwise the previously synced list would be invisible.
  const [okrMode, setOkrMode] = useState<'employee' | 'sync'>(() =>
    value.okrItems.some(it => (it.name ?? '').trim() !== '') ? 'sync' : 'employee'
  );
  const [showLevelOverride, setShowLevelOverride] = useState(false);

  // OKR Tracker units — loaded once per form open, cached at the service level.
  const [okrUnits, setOkrUnits] = useState<OkrTrackerUnit[]>([]);
  const [okrUnitsLoading, setOkrUnitsLoading] = useState(false);
  const [okrUnitsError, setOkrUnitsError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setOkrUnitsLoading(true);
    setOkrUnitsError(null);
    listUnits()
      .then(u => { if (alive) setOkrUnits(u); })
      .catch(e => { if (alive) setOkrUnitsError((e as Error).message); })
      .finally(() => { if (alive) setOkrUnitsLoading(false); });
    return () => { alive = false; };
  }, []);

  const directoryById = useMemo(() => {
    const map = new Map<string, DirectoryEmployee>();
    directory?.forEach(d => map.set(d.id, d));
    return map;
  }, [directory]);

  const pickEmployeeFromDir = (de: DirectoryEmployee) => {
    const sup = de.directManagerId ? directoryById.get(de.directManagerId) : undefined;
    // Auto-suggest OKR Tracker unit by fuzzy-matching MS Graph unitName.
    // HR can still override via the OKR Dept picker.
    const suggestedOkrUnit = de.unitName && okrUnits.length > 0
      ? fuzzyMatchUnit(okrUnits, de.unitName)
      : null;
    onChange({
      ...value,
      employeeId:       de.id,
      employeeEmail:    de.email,
      employeeName:     de.name,
      level:            (de.level as Level) ?? 'general',
      // Canonical form header — auto-filled, zero typing (EPR Form.docx)
      position:         de.jobTitle,
      department:       de.unitName,
      okrTrackerUnitId: suggestedOkrUnit?.id ?? value.okrTrackerUnitId,
      ...(sup ? {
        supervisorId:    sup.id,
        supervisorEmail: sup.email,
        supervisorName:  sup.name,
      } : {
        // Keep whatever was previously typed if no supervisor in directory
      }),
    });
  };

  const pickSupervisorFromDir = (de: DirectoryEmployee) => {
    update({
      supervisorId:    de.id,
      supervisorEmail: de.email,
      supervisorName:  de.name,
    });
  };

  const addUnmatchedEmployee = (search: string) => {
    const isEmail = search.includes('@');
    update({
      employeeId: `ext-${Date.now()}`,
      employeeEmail: isEmail ? search : `${search.replace(/\s+/g, '.').toLowerCase()}@example.com`,
      employeeName: isEmail ? search.split('@')[0] : search,
      level: 'general',
      position: '',
      department: '',
      okrTrackerUnitId: undefined
    });
  };

  const addUnmatchedSupervisor = (search: string) => {
    const isEmail = search.includes('@');
    update({
      supervisorId: `sup-${Date.now()}`,
      supervisorEmail: isEmail ? search : `${search.replace(/\s+/g, '.').toLowerCase()}@example.com`,
      supervisorName: isEmail ? search.split('@')[0] : search,
    });
  };

  // "Change" on the selected-person card — clears the pick and brings the
  // search box back. Changing the employee also drops synced OKR rows,
  // which belong to the previous person.
  const clearEmployee = () => update({
    employeeId: '', employeeEmail: '', employeeName: '',
    position: '', department: '',
    okrTrackerUnitId: undefined,
    okrItems: [],
  });
  const clearSupervisor = () => update({
    supervisorId: '', supervisorEmail: '', supervisorName: '',
  });

  const valid = validateEmployee(value);

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-stone-900">
          {isEdit ? 'Edit employee' : 'Add employee'}
        </div>
      </div>

      {/* Two-column: Employee + Supervisor side-by-side when there's
          room. Reduces vertical real-estate by ~50% vs stacked sections. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Employee">
          {useDirectory ? (
            !value.employeeId ? (
              <DirectoryPicker
                directory={directory!}
                selectedId={value.employeeId}
                placeholder="Search by name or email…"
                onPick={pickEmployeeFromDir}
                allowAddUnmatched
                onAddUnmatched={addUnmatchedEmployee}
              />
            ) : (
              <SelectedPersonCard
                name={value.employeeName}
                email={value.employeeEmail}
                subtitle={[value.position, value.department].filter(Boolean).join(' · ') || undefined}
                external={value.employeeId.startsWith('ext-')}
                onClear={clearEmployee}
              >
                {value.employeeId.startsWith('ext-') && (
                  <div className="mt-2 space-y-2">
                    <TextField label="Full Name" value={value.employeeName} onChange={v => update({ employeeName: v })} />
                    <TextField label="Email Address" value={value.employeeEmail} onChange={v => update({ employeeEmail: v })} type="email" />
                  </div>
                )}
              </SelectedPersonCard>
            )
          ) : (
            <div className="space-y-2">
              <TextField label="Name *"  value={value.employeeName}  onChange={v => update({ employeeName: v })} />
              <TextField label="Email *" value={value.employeeEmail} onChange={v => update({ employeeEmail: v })} type="email" />
              <TextField label="ID *"    value={value.employeeId}    onChange={v => update({ employeeId: v })} placeholder="emp-001" />
            </div>
          )}
        </Section>

        <Section title="Supervisor">
          {useDirectory ? (
            !value.supervisorId ? (
              <DirectoryPicker
                directory={directory!}
                selectedId={value.supervisorId}
                placeholder="Search by name or email…"
                onPick={pickSupervisorFromDir}
                excludeId={value.employeeId}
                allowAddUnmatched
                onAddUnmatched={addUnmatchedSupervisor}
              />
            ) : (
              <SelectedPersonCard
                name={value.supervisorName}
                email={value.supervisorEmail}
                external={value.supervisorId.startsWith('sup-')}
                onClear={clearSupervisor}
              >
                {value.supervisorId.startsWith('sup-') && (
                  <div className="mt-2 space-y-2">
                    <TextField label="Supervisor Name" value={value.supervisorName} onChange={v => update({ supervisorName: v })} />
                    <TextField label="Email Address" value={value.supervisorEmail} onChange={v => update({ supervisorEmail: v })} type="email" />
                  </div>
                )}
              </SelectedPersonCard>
            )
          ) : (
            <div className="space-y-2">
              <TextField label="Name *"  value={value.supervisorName}  onChange={v => update({ supervisorName: v })} />
              <TextField label="Email *" value={value.supervisorEmail} onChange={v => update({ supervisorEmail: v })} type="email" />
              <TextField label="ID *"    value={value.supervisorId}    onChange={v => update({ supervisorId: v })} placeholder="mgr-001" />
            </div>
          )}
        </Section>
      </div>

      {/* Profile (auto from directory) + OKR Dept side-by-side. The level
          is derived from the directory record — HR no longer picks it up
          front; the right template (with/without Leadership) follows
          automatically. A small override stays available for edge cases. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {useDirectory ? (
          <Section title="Profile (auto)">
            <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700 space-y-0.5">
              <div><span className="text-stone-500">Position:</span> {value.position || '—'}</div>
              <div><span className="text-stone-500">Department:</span> {value.department || '—'}</div>
              <div>
                <span className="text-stone-500">Level:</span> {LEVEL_LABELS[value.level]}
                {value.level === 'general'
                  ? ' · Leadership skipped (OKR 33 / KPI 44 / Core 22)'
                  : ' · Leadership included (OKR 30 / KPI 40 / Core 20 / Lead 10)'}
              </div>
            </div>
            {!showLevelOverride ? (
              <button
                type="button"
                onClick={() => setShowLevelOverride(true)}
                className="mt-1 text-xs text-stone-500 hover:text-stone-800 underline"
              >
                Adjust level
              </button>
            ) : (
              <select
                value={value.level}
                onChange={e => update({ level: e.target.value as Level })}
                className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm"
              >
                {LEVEL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
          </Section>
        ) : (
          <Section title="Profile">
            <div className="space-y-2">
              <TextField label="Position" value={value.position ?? ''} onChange={v => update({ position: v })} placeholder="e.g. Sales Manager" />
              <TextField label="Department" value={value.department ?? ''} onChange={v => update({ department: v })} placeholder="e.g. Commercial" />
              <label className="block">
                <div className="text-xs text-stone-600 mb-1">Level</div>
                <select
                  value={value.level}
                  onChange={e => update({ level: e.target.value as Level })}
                  className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm"
                >
                  {LEVEL_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {value.level === 'general' && (
                  <div className="text-xs text-stone-500 mt-1">
                    Leadership skipped. Weights → OKR 33% / KPI 44% / Core 22%.
                  </div>
                )}
              </label>
            </div>
          </Section>
        )}

        <Section title="OKR Dept">
          {okrUnitsLoading && (
            <div className="text-xs text-stone-500">Loading…</div>
          )}
          {okrUnitsError && (
            <div className="text-xs text-red-700 rounded bg-red-50 border border-red-200 px-2 py-1.5">
              {okrUnitsError}
            </div>
          )}
          {!okrUnitsLoading && !okrUnitsError && (
            <select
              value={value.okrTrackerUnitId ?? ''}
              onChange={e => update({ okrTrackerUnitId: e.target.value || undefined })}
              className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">— Not set —</option>
              {okrUnits.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.code ? ` (${u.code})` : ''}
                </option>
              ))}
            </select>
          )}
        </Section>
      </div>

      {/* Cascade tracer — only shown AFTER both employee and supervisor are
          set, so it doesn't appear early and clutter the initial state. */}
      {value.employeeEmail && value.supervisorEmail && (
        <Section title="Cascaded tasks (from OKR Tracker)">
          <CascadeTimeline email={value.employeeEmail} year={cycleYear} compact />
        </Section>
      )}

      <Section title="OKR Settings">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className={`cursor-pointer rounded-lg border p-3 flex gap-3 transition-colors ${okrMode === 'employee' ? 'border-emerald-500 bg-emerald-50/30' : 'border-stone-200 hover:bg-stone-50'}`}>
            <input
              type="radio"
              name="okrMode"
              value="employee"
              checked={okrMode === 'employee'}
              onChange={() => {
                setOkrMode('employee');
                // Drop any previously synced rows so they don't ship silently.
                update({ okrItems: [] });
              }}
              className="mt-0.5 accent-emerald-600"
            />
            <div>
              <div className="text-sm font-medium text-stone-900">Employee will fill their own OKRs</div>
              <div className="text-xs text-stone-500 mt-0.5">They pick their OKRs in their EPR form.</div>
            </div>
          </label>
          <label className={`cursor-pointer rounded-lg border p-3 flex gap-3 transition-colors ${okrMode === 'sync' ? 'border-emerald-500 bg-emerald-50/30' : 'border-stone-200 hover:bg-stone-50'}`}>
            <input
              type="radio"
              name="okrMode"
              value="sync"
              checked={okrMode === 'sync'}
              onChange={() => setOkrMode('sync')}
              className="mt-0.5 accent-emerald-600"
            />
            <div>
              <div className="text-sm font-medium text-stone-900">Sync OKRs from OKR Tracker</div>
              <div className="text-xs text-stone-500 mt-0.5">Auto-pulled for this employee, scoped by OKR Dept. Untick anything not relevant.</div>
            </div>
          </label>
        </div>
      </Section>

      {okrMode === 'sync' && (
        <div className="pt-2 border-t border-stone-100">
          <OkrSyncPanel
            email={value.employeeEmail}
            cycleYear={cycleYear}
            cyclePeriod={cyclePeriod}
            level={value.level as EmployeeLevelForSync}
            okrTrackerUnitId={value.okrTrackerUnitId}
            okrUnits={okrUnits}
            items={value.okrItems}
            onChangeItems={items => update({ okrItems: items })}
          />
        </div>
      )}

      {/* KPI section removed intentionally per UX redesign */}

      <div className="flex gap-2 justify-end pt-2 border-t border-stone-200">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="submit" disabled={!valid} onClick={onSave}>
          {isEdit ? 'Save changes' : 'Add to list'}
        </Button>
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, actions, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</div>
      {actions}
    </div>
    {children}
  </div>
);

/**
 * SelectedPersonCard — replaces the search input once a person is picked,
 * so "selected" is unmistakable: emerald card, avatar initials, check
 * icon, bold name. "Change" clears the pick and brings the search back.
 * `external` marks contacts added outside the directory; children render
 * the editable name/email fields for those.
 */
const SelectedPersonCard: React.FC<{
  name: string;
  email: string;
  subtitle?: string;
  external?: boolean;
  onClear: () => void;
  children?: React.ReactNode;
}> = ({ name, email, subtitle, external, onClear, children }) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]!.toUpperCase())
    .join('') || '?';
  return (
    <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 px-3 py-2.5">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="material-symbols-rounded text-emerald-600" style={{ fontSize: 16 }}>
              check_circle
            </span>
            <span className="text-sm font-semibold text-stone-900 truncate">
              {name || '(unnamed)'}
            </span>
            {external && (
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded-full border bg-amber-50 text-amber-800 border-amber-200 flex-shrink-0">
                Outside directory
              </span>
            )}
          </div>
          <div className="text-xs text-stone-600 truncate">{email || 'no email'}</div>
          {subtitle && <div className="text-xs text-stone-500 truncate">{subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-stone-500 hover:text-stone-900 underline underline-offset-2 flex-shrink-0 mt-0.5"
        >
          Change
        </button>
      </div>
      {children}
    </div>
  );
};

const DirectoryPicker: React.FC<{
  directory: DirectoryEmployee[];
  selectedId?: string;
  placeholder?: string;
  onPick: (de: DirectoryEmployee) => void;
  excludeId?: string;
  allowAddUnmatched?: boolean;
  onAddUnmatched?: (search: string) => void;
}> = ({ directory, selectedId, placeholder, onPick, excludeId, allowAddUnmatched, onAddUnmatched }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return directory
      .filter(d => d.id !== excludeId)
      .filter(d => !q || d.name.toLowerCase().includes(q) || d.email.toLowerCase().includes(q))
      .slice(0, 20);
  }, [directory, search, excludeId]);

  return (
    <div className="relative">
      <input
        type="text"
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder ?? 'Search…'}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg">
          {filtered.map(d => (
            <button
              key={d.id}
              type="button"
              onMouseDown={() => { onPick(d); setSearch(''); setOpen(false); }}
              className={[
                'w-full text-left px-3 py-2 text-sm hover:bg-stone-100 flex items-center justify-between gap-3',
                d.id === selectedId ? 'bg-emerald-50' : '',
              ].join(' ')}
            >
              <div className="min-w-0">
                <div className="font-medium text-stone-900 truncate">{d.name}</div>
                <div className="text-xs text-stone-500 truncate">{d.email}{d.jobTitle ? ` · ${d.jobTitle}` : ''}</div>
              </div>
              {d.id === selectedId && (
                <span className="material-symbols-rounded text-emerald-600" style={{ fontSize: 18 }}>check</span>
              )}
            </button>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && search.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-stone-200 bg-white overflow-hidden shadow-lg">
          {allowAddUnmatched ? (
            <button
              type="button"
              onMouseDown={() => { onAddUnmatched?.(search); setSearch(''); setOpen(false); }}
              className="w-full text-left px-3 py-3 text-sm text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 font-medium"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 18 }}>add_circle</span>
              Add "{search}" as new contact
            </button>
          ) : (
            <div className="px-3 py-2 text-xs text-stone-500">No match found.</div>
          )}
        </div>
      )}
    </div>
  );
};

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}> = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <label className="block">
    <div className="text-xs text-stone-600 mb-1">{label}</div>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
    />
  </label>
);

function validateEmployee(e: Employee): boolean {
  return !!(e.employeeId && e.employeeEmail && e.employeeName
    && e.supervisorId && e.supervisorEmail && e.supervisorName);
}

// ─── Inline OKR sync panel ────────────────────────────────────────

/**
 * OkrSyncPanel — zero-click OKR sync. Fetches the employee's KRs from
 * OKR Tracker the moment the "Sync OKRs" mode is selected and re-fetches
 * automatically when the OKR Dept, level, or employee changes. All KRs
 * arrive pre-selected; selection writes straight into `okrItems`, so
 * there is no modal and no Apply step.
 *
 * The OKR Dept dropdown on the form IS the scope filter — when set it is
 * passed as the manual unit allowlist, replacing the old in-modal
 * unit-chip picker.
 */
const OkrSyncPanel: React.FC<{
  email: string;
  cycleYear?: number;
  cyclePeriod?: EprPeriod;
  level: EmployeeLevelForSync;
  okrTrackerUnitId?: string;
  okrUnits: OkrTrackerUnit[];
  items: Employee['okrItems'];
  onChangeItems: (items: Employee['okrItems']) => void;
}> = ({ email, cycleYear, cyclePeriod, level, okrTrackerUnitId, okrUnits, items, onChangeItems }) => {
  const [krs, setKrs] = useState<SyncedKr[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [rawCount, setRawCount] = useState(0);
  const [diagnostics, setDiagnostics] = useState<FetchKrsForEmployeeResult['diagnostics'] | null>(null);
  const [unitSource, setUnitSource] = useState<string | null>(null);
  const [homeUnitNames, setHomeUnitNames] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Refs keep the effect's dependency list down to the primitives that
  // actually define a sync (per ENGINEERING.md: no unstable deps).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onChangeRef = useRef(onChangeItems);
  onChangeRef.current = onChangeItems;

  useEffect(() => {
    if (!email || !cycleYear || !cyclePeriod) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchKrsForEmployee({
      email,
      year:          cycleYear,
      period:        cyclePeriod,
      level,
      scopeByUnit:   true,
      // The OKR Dept dropdown wins over the auto-resolved home unit.
      manualUnitIds: okrTrackerUnitId ? [okrTrackerUnitId] : undefined,
    })
      .then(r => {
        if (!alive) return;
        setKrs(r.krs);
        setOwnerId(r.ownerId);
        setRawCount(r.rawCount);
        setDiagnostics(r.diagnostics);
        setUnitSource(r.user?.unitSource ?? null);
        setHomeUnitNames(r.user?.unitNames ?? []);
        // Fresh employee (no rows yet): pre-select everything so the
        // happy path is literally zero clicks. When re-editing, the
        // saved selection is kept as-is.
        if (itemsRef.current.every(it => (it.name ?? '').trim() === '')) {
          onChangeRef.current(r.krs.map(toOkrSeed));
        }
      })
      .catch(e => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [email, cycleYear, cyclePeriod, level, okrTrackerUnitId, refreshKey]);

  const pickedIds = useMemo(() => new Set(items.map(i => i.id)), [items]);

  const toggle = useCallback((kr: SyncedKr) => {
    const kept = itemsRef.current.filter(i => (i.name ?? '').trim() !== '');
    if (pickedIds.has(kr.krId)) {
      onChangeRef.current(kept.filter(i => i.id !== kr.krId));
    } else {
      onChangeRef.current([...kept, toOkrSeed(kr)]);
    }
  }, [pickedIds]);

  // Saved rows that the current sync no longer returns (period changed,
  // KR archived, dept switched). Shown explicitly so nothing disappears
  // silently from a previously saved employee.
  const staleItems = useMemo(
    () => items.filter(i =>
      (i.name ?? '').trim() !== '' && !krs.some(k => k.krId === i.id)),
    [items, krs],
  );

  const deptName = okrTrackerUnitId
    ? okrUnits.find(u => u.id === okrTrackerUnitId)?.name
    : null;

  if (!email) {
    return (
      <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 text-sm text-stone-600">
        Pick the employee first — the sync uses their email to find their OKRs.
      </div>
    );
  }
  if (!cycleYear || !cyclePeriod) {
    return (
      <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 text-sm text-stone-600">
        Set the cycle year and period first — they scope which KRs are pulled.
      </div>
    );
  }

  return (
    <Section
      title="OKRs from OKR Tracker"
      actions={
        <Button variant="tertiary" icon="cloud_sync" onClick={() => setRefreshKey(k => k + 1)} disabled={loading}>
          Refresh
        </Button>
      }
    >
      <div className="text-xs text-stone-500 mb-2">
        {cyclePeriod} {cycleYear}
        {deptName
          ? <> · scoped to OKR Dept <b className="text-stone-700">{deptName}</b></>
          : homeUnitNames.length > 0
            ? <> · home unit <b className="text-stone-700">{homeUnitNames.join(', ')}</b></>
            : null}
        {' · '}
        {level === 'general'
          ? 'cascaded KRs only (General level)'
          : 'owned + cascaded KRs'}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-stone-600 py-2">
          <span className="material-symbols-rounded animate-spin text-stone-400" style={{ fontSize: 18 }}>
            progress_activity
          </span>
          Syncing from okr.rascalrepublic.com…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          Could not sync: {error}
          <button
            type="button"
            onClick={() => setRefreshKey(k => k + 1)}
            className="ml-2 font-medium underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && ownerId === null && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
          No OKR Tracker account found for <code>{email}</code>. Ask them to sign in
          to <a className="underline" href="https://okr.rascalrepublic.com" target="_blank" rel="noreferrer">okr.rascalrepublic.com</a> once,
          or switch to “Employee will fill their own OKRs”.
        </div>
      )}

      {!loading && !error && ownerId && unitSource === 'none' && !okrTrackerUnitId && level !== 'general' && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 mb-2">
          OKR Tracker has no home unit for this user — select an <b>OKR Dept</b> above
          to scope the list to the right department.
        </div>
      )}

      {!loading && !error && ownerId && krs.length === 0 && (
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-sm text-stone-700">
          Account found but no KRs match {cyclePeriod} {cycleYear}
          {deptName ? <> in <b>{deptName}</b></> : null}.
          {rawCount > 0 && (
            <> {rawCount} KR{rawCount === 1 ? '' : 's'} exist for this user outside this filter.</>
          )}
          {deptName ? ' Try a different OKR Dept above.' : ''}
        </div>
      )}

      {!loading && !error && krs.length > 0 && (
        <div className="space-y-2">
          {krs.map(k => {
            const picked = pickedIds.has(k.krId);
            return (
              <label
                key={k.krId}
                className={[
                  'flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors',
                  picked ? 'border-emerald-300 bg-emerald-50/50' : 'border-stone-200 hover:bg-stone-50',
                ].join(' ')}
              >
                <input
                  type="checkbox"
                  checked={picked}
                  onChange={() => toggle(k)}
                  className="mt-1 accent-emerald-700"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-stone-900 flex items-center gap-2 flex-wrap">
                    {k.krNumber && <span className="text-stone-500 mr-1">{k.krNumber}</span>}
                    <span className="truncate">{k.title}</span>
                    <span className={[
                      'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded-full border',
                      k.bucket === 'owner'
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-indigo-50 text-indigo-800 border-indigo-200',
                    ].join(' ')}>
                      {k.bucket === 'owner' ? 'Owner' : 'Assigned'}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5 truncate">
                    {k.objectiveTitle}
                    {k.quarters.length > 0 && <> · {k.quarters.join(', ')}</>}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {!loading && staleItems.length > 0 && (
        <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
          <div className="text-xs font-medium text-stone-600 mb-1">
            Saved earlier but not in the current sync (kept unless removed):
          </div>
          <div className="space-y-1">
            {staleItems.map(i => (
              <div key={i.id} className="flex items-center justify-between gap-2 text-sm text-stone-700">
                <span className="truncate">{i.name}</span>
                <button
                  type="button"
                  onClick={() => onChangeRef.current(
                    itemsRef.current.filter(x => x.id !== i.id))}
                  className="text-xs text-stone-500 hover:text-red-700 underline flex-shrink-0"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !error && (krs.length > 0 || diagnostics) && (
        <div className="mt-2 text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-1">
          {krs.length > 0 && (
            <span><b>{[...pickedIds].filter(id => krs.some(k => k.krId === id)).length}</b> of {krs.length} selected — these become the OKR rows on the EPR form</span>
          )}
          {diagnostics && diagnostics.droppedOutOfUnit > 0 && (
            <span className="text-stone-400">· {diagnostics.droppedOutOfUnit} hidden (other dept)</span>
          )}
          {diagnostics && diagnostics.droppedWrongYear > 0 && (
            <span className="text-stone-400">· {diagnostics.droppedWrongYear} hidden (other year)</span>
          )}
          {diagnostics && diagnostics.droppedWrongPeriod > 0 && (
            <span className="text-stone-400">· {diagnostics.droppedWrongPeriod} hidden (outside {cyclePeriod})</span>
          )}
        </div>
      )}
    </Section>
  );
};

export default EmployeeBuilder;
